import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { fetchAppSettings, fetchToken } from "../services/api";
import Unauthorized from "../components/Unauthorized";
import CustomSpinner from "../components/CustomSpinner";
import { getChatClient } from "../utils/streamChatClient";
const AppSettingsContext = createContext();
import StorageService from "../utils/StorageService";
import { useAppEnv } from '../context/AppEnvContext';
export const AppSettingsProvider = ({
  token,
  children,
}) => {
  const env = useAppEnv();
  const [settings, setSettings] = useState({
    backgroundColor: "#ffffff",
    textColor: "#333333",
    buttonColor: "#0077cc",
  });
  const [tokens, setTokens] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [chatClient, setChatClient] = useState(null);
  const setToken = (userId, token) => {
    setTokens((prevTokens) => {
      if (prevTokens[userId] === token) return prevTokens; // Avoid unnecessary updates
      return { ...prevTokens, [userId]: token };
    });
  };
  // Memoize the StreamChat client

  const memoizedChatClient = useMemo(() => getChatClient(env.REACT_APP_STREAM_KEY), []);
  // Use useRef to persist the isConnecting flag across renders
  const isConnecting = useRef(false);
  useEffect(() => {
    const initialize = async () => {
      try {       
        const _guard_ = StorageService.get("_guard_");  
        if (!_guard_) {
          throw new Error("Invalid request");
        }
      
        setChatClient(memoizedChatClient); // Set the client in state
        // Fetch app settings
        const appSettings = await fetchAppSettings(0);

        setSettings({
          backgroundColor: appSettings.navBackgroundColor,
          textColor: appSettings.navForegroundColor,
          buttonColor: appSettings.loginButtonColor,
        });

      
        let token = StorageService.get(`_stream_${_guard_}`);
         if(!token){ 
          const url = `${env.REACT_APP_API_BASE_URL}/${env.REACT_APP_TOKEN_URL}`; 
          // Pass token in fetchToken headers
          const response = await fetchToken(url, StorageService.get(`system_${_guard_}`)); 
          StorageService.set(`_stream_${_guard_}`, response.token);
          setToken(`_stream_${_guard_}`, response.token);
         }else{
          setToken(`_stream_${_guard_}`, token);
         }
         

        // Connect user only if not already connected
        if (!memoizedChatClient.userID && !isConnecting.current) {
          isConnecting.current = true; // Set the flag to true
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Add delay

          isConnecting.current = false; // Reset the flag
        }

        setLoading(false);
      } catch (err) {
        console.error("Error initializing AppSettingsContext:", err);
        setError(true);
        setLoading(false);
      }
    };

    initialize();
    return () => {
      if (memoizedChatClient) memoizedChatClient.disconnectUser(); // Clean up on unmount
    };
  }, [token, memoizedChatClient]);

  if (loading) {
    return <CustomSpinner />;
  }

  if (error) {
    return <Unauthorized />;
  }

  return (
    <AppSettingsContext.Provider value={{ settings, tokens, chatClient }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
